import { useEffect, useState } from "react";
import { Buildings, X, WarningCircle, Warning, CheckCircle, FileText, Certificate, Receipt, ArrowSquareOut, Info } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Philly's CARTO endpoints sometimes return dates as ISO strings, other
// times as UNIX millis integers or year integers. `.slice()` on a number
// throws exactly what an admin saw: "(t || "").slice is not a function".
// Coerce to string first — worst case gives us the numeric string, which
// slice happily handles.
const safeDate = v => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string") return v.slice(0, 10);
  try {
    // Number → epoch ms (or seconds if too small)
    if (typeof v === "number") {
      const ms = v < 10_000_000_000 ? v * 1000 : v;
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      return String(v).slice(0, 10);
    }
    return String(v).slice(0, 10);
  } catch { return String(v).slice(0, 10); }
};

/**
 * PhillyComplianceModal — live snapshot of L&I violations, permits, business
 * licenses, tax status, and lead-cert flag pulled from Philadelphia's public
 * open-data CARTO endpoints. Powered by /api/compliance/philly/lookup.
 * Works for any address in Philly.
 */
export default function PhillyComplianceModal({ address, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api.get("/compliance/philly/lookup", { params: { address }})
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Compliance lookup failed"));
  }, [address]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="philly-compliance-modal">
      <div className="w-full sm:max-w-3xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
        <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <Buildings weight="fill" size={22} className="text-cyan-700"/>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-lg uppercase tracking-tight">Philly Compliance</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate" data-testid="pcm-address">{address}</div>
          </div>
          {data?.atlas_url && (
            <a href={data.atlas_url} target="_blank" rel="noreferrer" data-testid="pcm-atlas-link"
              className="hidden sm:inline-flex h-9 px-3 items-center gap-1 border-2 border-[#0f172a] font-black uppercase text-[9px] tracking-widest hover:bg-slate-50">
              Atlas <ArrowSquareOut size={11} weight="bold"/>
            </a>
          )}
          <button onClick={onClose} data-testid="pcm-close"
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded"><X size={20}/></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {err && (
            <div className="text-center py-8" data-testid="pcm-error">
              <WarningCircle weight="bold" size={36} className="mx-auto text-red-500"/>
              <div className="mt-2 font-display font-black text-lg">{err}</div>
            </div>
          )}
          {!err && !data && (
            <div className="text-center py-10 text-sm font-black uppercase tracking-widest text-slate-500" data-testid="pcm-loading">Querying Philly open data…</div>
          )}
          {data && !data.found && (
            <div className="text-center py-8" data-testid="pcm-not-found">
              <Info weight="bold" size={36} className="mx-auto text-amber-500"/>
              <div className="mt-2 font-display font-black text-lg">No Match on Philly's Records</div>
              <div className="mt-1 text-sm text-slate-600">{data.reason || "Try the exact street spelling."}</div>
            </div>
          )}
          {data?.found && (
            <div className="space-y-5">
              {/* OPA snapshot */}
              <section data-testid="pcm-opa">
                <SectionTitle label="Property (OPA)" icon={<FileText size={14}/>} tint="text-slate-700"/>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Kpi label="Parcel" value={data.parcel_number}/>
                  <Kpi label="Year Built" value={data.opa?.year_built || "—"} accent={data.lead_cert_required ? "text-red-600" : ""}/>
                  <Kpi label="Market Value" value={data.opa?.market_value ? `$${Number(data.opa.market_value).toLocaleString()}` : "—"}/>
                  <Kpi label="Building" value={data.opa?.building_code || "—"} small/>
                </div>
                <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Owner · {data.opa?.owner || "—"}</div>
                {data.lead_cert_required && (
                  <div className="mt-3 p-2 border-l-4 border-red-500 bg-red-50 text-xs font-bold text-red-800" data-testid="pcm-lead-flag">
                    ⚠︎ Pre-1978 — Lead certification required for rental license (upload cert to phila.gov every 4 years).
                  </div>
                )}
              </section>

              {/* Violations */}
              <Section
                testid="pcm-violations"
                title="L&I Violations"
                icon={<Warning size={14} weight="bold"/>}
                stat={data.violations.open > 0 ? `${data.violations.open} open · ${data.violations.total} total` : `${data.violations.total} historical`}
                tint={data.violations.open > 0 ? "text-red-700 border-red-500 bg-red-50" : "text-emerald-700 border-emerald-500 bg-emerald-50"}
                empty="Clean · no L&I violations on record."
                items={data.violations.items}
                render={(x, i) => (
                  <Row key={i} testid={`pcm-violation-${i}`}
                    date={x.violationdate} status={x.violationstatus}
                    title={x.violationcodetitle}
                    sub={`Case #${x.casenumber} · ${x.caseprioritydesc || "STANDARD"}${x.unit_num ? ` · Unit ${x.unit_num}` : ""}${x.violationresolutiondate ? ` · Resolved ${safeDate(x.violationresolutiondate)}` : ""}`}
                    isBad={!(x.violationresolutiondate)}
                  />
                )}
              />

              {/* Permits */}
              <Section
                testid="pcm-permits"
                title="L&I Permits (non-admin)"
                icon={<FileText size={14} weight="bold"/>}
                stat={`${data.permits.total} total`}
                tint="text-amber-700 border-amber-500 bg-amber-50"
                empty="No permits pulled at this address."
                items={data.permits.items}
                render={(x, i) => (
                  <Row key={i} testid={`pcm-permit-${i}`}
                    date={x.permitissuedate} status={x.status}
                    title={`${x.permittype} · ${x.permitdescription || ""}`}
                    sub={`Permit #${x.permitnumber}${x.applicanttype ? ` · ${x.applicanttype}` : ""}${x.unit_num ? ` · Unit ${x.unit_num}` : ""}`}
                  />
                )}
              />

              {/* Licenses */}
              <Section
                testid="pcm-licenses"
                title="Business / Rental Licenses"
                icon={<Certificate size={14} weight="bold"/>}
                stat={`${data.licenses.active} active · ${data.licenses.total} total`}
                tint="text-cyan-700 border-cyan-500 bg-cyan-50"
                empty="No business or rental license records at this parcel."
                items={data.licenses.items}
                render={(x, i) => (
                  <Row key={i} testid={`pcm-license-${i}`}
                    date={x.expirationdate} status={x.licensestatus}
                    title={x.licensetype}
                    sub={`License #${x.licensenumber} · Issued ${safeDate(x.initialissuedate)}${x.unit_num ? ` · Unit ${x.unit_num}` : ""}`}
                    isBad={(x.licensestatus || "").toUpperCase() === "EXPIRED"}
                  />
                )}
              />

              {/* Tax */}
              <Section
                testid="pcm-tax"
                title="Real Estate Tax"
                icon={<Receipt size={14} weight="bold"/>}
                stat={data.tax.records > 0 ? `${data.tax.records} balance record${data.tax.records === 1 ? "" : "s"}` : "Current · no balance owed"}
                tint={data.tax.records > 0 ? "text-red-700 border-red-500 bg-red-50" : "text-emerald-700 border-emerald-500 bg-emerald-50"}
                empty="Tax is current — no outstanding balance found."
                items={data.tax.items}
                render={(x, i) => {
                  const due = x.total_due || x.total_balance_due || x.balance || 0;
                  return (
                    <Row key={i} testid={`pcm-tax-${i}`}
                      date={x.tax_period_end || x.tax_period || ""}
                      status={x.tax_status || "OWED"}
                      title={`$${Number(due).toLocaleString()} owed`}
                      sub={x.principal_balance || x.penalty ? `Principal $${Number(x.principal_balance || 0).toLocaleString()} · Interest $${Number(x.interest_balance || x.interest || 0).toLocaleString()} · Penalty $${Number(x.penalty_balance || x.penalty || 0).toLocaleString()}` : ""}
                      isBad={true}
                    />
                  );
                }}
              />

              <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-200">
                Source: <a href="https://opendataphilly.org/" target="_blank" rel="noreferrer" className="underline">OpenDataPhilly</a> · Live at query time
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = "text-slate-900", small = false }) {
  return (
    <div className="p-2 border-2 border-slate-300 bg-white">
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-0.5 font-display font-black ${small ? "text-xs leading-tight" : "text-lg"} ${accent}`}>{value}</div>
    </div>
  );
}

function SectionTitle({ label, icon, tint = "text-slate-700" }) {
  return (
    <div className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest ${tint}`}>
      {icon}{label}
    </div>
  );
}

function Section({ title, icon, stat, tint, items, render, empty, testid }) {
  return (
    <section data-testid={testid}>
      <div className={`p-2 border-l-4 ${tint} flex items-center gap-2`}>
        {icon}
        <div className="font-display font-black text-sm uppercase tracking-wider">{title}</div>
        <div className="ml-auto text-[10px] font-black uppercase tracking-widest">{stat}</div>
      </div>
      <div className="mt-2 space-y-1">
        {items.length === 0 ? (
          <div className="text-xs text-slate-500 font-bold px-2 py-1">{empty}</div>
        ) : items.map((x, i) => render(x, i))}
      </div>
    </section>
  );
}

function Row({ date, status, title, sub, isBad, testid }) {
  const badge = (status || "").toUpperCase();
  const badgeCls = isBad ? "bg-red-600 text-white" : (badge === "COMPLETED" || badge === "ISSUED" || badge === "ACTIVE") ? "bg-emerald-600 text-white" : "bg-slate-500 text-white";
  return (
    <div className="p-2 border border-slate-200 hover:bg-slate-50 flex items-start gap-2" data-testid={testid}>
      <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${badgeCls}`}>{badge || "—"}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-black text-slate-900 truncate">{title || "—"}</div>
        <div className="text-[10px] text-slate-500 truncate">{sub}</div>
      </div>
      <div className="shrink-0 text-[10px] font-black uppercase tracking-widest text-slate-500">{safeDate(date)}</div>
    </div>
  );
}
